import type {
  AskQuote,
  AskScope,
  AskSource,
  AskUsage,
  AvailableConcept,
  ConceptSeries,
  DocumentTable,
  FilingTermChange,
  Flag,
  IndexMembershipResult,
  InsiderActivity,
  MarketCapResult,
  SituationDetail,
  SituationEvent,
  SituationSummary,
  Excerpt,
  OpenQuestion,
  Thesis,
  ThesisVersion,
} from "@mosaic/shared";
import { askScopeQuery } from "../lib/askScope.js";

/**
 * The typed client for the contract in API-CONTRACT.md.
 *
 * Response types come from `@mosaic/shared`, which the server also builds its rows from,
 * so a change to the domain shape fails to compile on both sides.
 */

export interface ListResponse<T> {
  items: T[];
  /** Null on a continuation page: a reader already scrolling does not need it recounted. */
  total: number | null;
  cursor: string | null;
}

/** The error envelope an API route answers with on failure. */
export interface ApiErrorBody {
  error: { code: string; message: string; detail?: unknown };
}

/**
 * A non-2xx answer from the API, carrying the envelope's message, code and detail where
 * the body had one.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly detail?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** The frontend decides how to present being signed out; the API never redirects. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

/**
 * Call an API route with the session cookie and return its parsed JSON body.
 *
 * @internal
 * @param path The route, with any query string.
 * @param init Fetch options; a body is sent as JSON.
 * @returns The parsed body, or undefined for a 204.
 * @throws {ApiError} On any non-2xx status, with code `unknown` when the body is not the
 *   error envelope.
 */
async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...init,
    // The session is a cookie. `same-origin` is the default; stating it makes a move to a
    // separate origin a deliberate change here.
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...init.headers,
    },
  });

  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // A non-JSON error body is itself the problem; fall through to the status.
    }
    /**
     * Not every non-2xx body is the error envelope: `/api/health` answers 503 with its
     * dependency report, `{ status, database, ollama, … }`.
     */
    const envelope =
      body !== null && typeof body === "object" && "error" in body
        ? (body as ApiErrorBody).error
        : null;
    throw new ApiError(
      envelope?.message ?? `${res.status} ${res.statusText}`,
      res.status,
      envelope?.code ?? "unknown",
      envelope?.detail,
    );
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * A query string from a parameter object, repeating a key for each array element.
 *
 * @internal
 * @param params Values to encode; undefined, null and empty-string values are omitted.
 * @returns The string with its leading `?`, or empty when nothing remains.
 */
function query(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) {
      for (const v of value) search.append(key, String(v));
    } else {
      search.set(key, String(value));
    }
  }
  const s = search.toString();
  return s ? `?${s}` : "";
}

/** The filters, sort and paging the situation list accepts. */
export interface SituationFilters {
  stage?: string[];
  flag?: string[];
  severity?: string;
  recordBefore?: string;
  recordAfter?: string;
  pinned?: boolean;
  archived?: boolean;
  /**
   * Repeated. Naming any value lifts the radar's default hiding of the two dismissals and
   * the default `archived=false`.
   */
  disposition?: string[];
  /** Every situation this company is on, either side — what the company page lists. */
  cik?: string;
  q?: string;
  sort?: string;
  limit?: number;
  cursor?: string;
}

/**
 * One extracted or owner-entered fact, with the document and offsets it was quoted from.
 * `supersededBy` is set when a newer fact of the same kind replaced it.
 */
export interface FactResponse {
  id: string;
  kind: string;
  value: unknown;
  quote: string | null;
  document: { id: string; accession: string; formType: string | null; filename: string | null } | null;
  charStart: number | null;
  charEnd: number | null;
  method: "rule" | "llm" | "user";
  confidence: number;
  /**
   * The filing offers this as assumed rather than stating it: "an assumed Distribution
   * ratio of 1-for-4" in a Subject-to-Completion draft. Independent of `confidence`, which is
   * the reader's certainty about its own reading; the hedge is the document's.
   */
  tentative: boolean;
  note: string | null;
  extractedAt: string;
  supersededBy: string | null;
}

/**
 * One side of a separation's financial history.
 *
 * `status` separates "fetching" (never asked for before), "empty" (asked, and this filer
 * tags none of the candidate concepts) and "unknown" (no CIK to ask about). Rendered as one
 * blank table, all three would read as "no history".
 */
export interface FinancialsSide {
  cik: string | null;
  name: string | null;
  status: "ready" | "fetching" | "empty" | "unknown";
  entityName?: string;
  fetchedAt?: string;
  /** The XBRL tag that answered. Shown, because a figure with no tag has no provenance. */
  resolved?: { taxonomy: string; concept: string } | null;
  candidates?: string[];
  series?: ConceptSeries | null;
  summary?: {
    periods: number;
    mean: string;
    min: string;
    max: string;
    restated: number;
  } | null;
  unavailable?: string | null;
}

/** A situation's reported history for one concept, for the parent and SpinCo side by side. */
export interface SituationFinancials {
  situationId: string;
  concept: string;
  basis: string;
  parent: FinancialsSide;
  spinco: FinancialsSide;
  /** The dimensionless-data caveat. Rendered, never swallowed. */
  limitation: string;
}

/** The payload of each event an Ask stream delivers, keyed by event name. */
export interface AskEventMap {
  context: { threadId: string; depth: string; model: string; strategy: string; preloadedChars: number; briefing: string };
  step: { tool: string; detail: string };
  delta: { text: string };
  citation: AskQuote;
  entity_warning: { message: string };
  done: { threadId: string; messageId: string; model: string; steps: number; sources: AskSource[]; usage: AskUsage };
  error: { code: string; message: string };
  /**
   * A `whole`-depth question was queued rather than answered. Not an SSE event — the
   * server answers 202 with this body and no stream — but delivered through the same
   * callback map so the panel has one place to react.
   */
  queued: {
    status: "queued";
    threadId: string;
    messageId: string;
    jobId: string | null;
    executor: "subscription";
    /** False means nothing can claim it, so the answer will simply never arrive. */
    agentConfigured: boolean;
  };
}

/** What an Ask question would read and cost at a given depth, quoted before it is asked. */
export interface AskPreflight {
  depth: string;
  model: string;
  documentCount: number;
  largestDocument: { accession: string; sequence: number; filename: string; charCount: number } | null;
  estimatedInputTokens: number;
  /** Dollars, as a decimal string. */
  estimatedUsd: string;
  /** True for the navigated depths: the model decides how much it reads, so this is a cap. */
  isCeiling: boolean;
}

/**
 * Where the corpus's disk went. `totalBytes` is the whole database and the groups sum
 * to it; `other` is the remainder. It excludes the raw filings, which are gzipped on disk
 * outside Postgres.
 */
export interface DatabaseSize {
  totalBytes: number;
  groups: Array<{
    key: "documents" | "embeddings" | "financials" | "other";
    label: string;
    bytes: number;
    tables: string[];
  }>;
  largest: Array<{ table: string; bytes: number }>;
  counts: {
    filings: number;
    documents: number;
    extracted: number;
    characters: number;
    chunks: number;
    embeddedDocuments: number;
  };
}

/**
 * The historical Form 10 pass, quoted before it starts. The counts are projected from a
 * rate, not measured: nothing knows the real count until the quarterly indexes are walked.
 */
export interface Form10BackfillEstimate {
  from: string;
  to: string;
  quarters: number;
  quartersDone: number;
  projectedFilings: number;
  projectedCharacters: number;
  projectedChunks: number;
  projectedEmbedHours: number;
  /** The rate the hours were computed from. */
  embedChunksPerSec: number;
  /**
   * False when no recent pass was big enough to measure and the fallback constant was
   * used. Embedding rates differ widely between hosts, so the hours are only as good as this.
   */
  embedRateMeasured: boolean;
  projectedEdgarRequests: number;
  queued: number;
  running: number;
  paused: boolean;
  /** Embedding is most of the cost; paused means the pass buys disk, not CPU-days. */
  embeddingPaused: boolean;
}

/**
 * The embedding backfill, costed before it is started: what is pending, what is in
 * flight, and the projected hours at a stated throughput.
 */
export interface BackfillEstimate {
  pending: number;
  /** What the predicate actually selected, so the count can be read rather than trusted. */
  byType: Array<{ documentType: string; documents: number; characters: number }>;
  queued: number;
  running: number;
  paused: boolean;
  embedded: number;
  characters: number;
  estimatedChunks: number;
  estimatedHours: number;
  /** The throughput the estimate used — it does not transfer between machines. */
  chunksPerSecond: number;
  /** `bench` is the script's figure; `observed` is this host's own last long pass. */
  chunksPerSecondSource: "bench" | "observed";
  /**
   * What the last pass did, reconstructed from `chunks.embedded_at`. A pass is many jobs
   * and no row groups them, so the chunk timestamps are the only record of a pass.
   */
  lastPass: {
    startedAt: string;
    finishedAt: string;
    chunks: number;
    documents: number;
    seconds: number;
    chunksPerSecond: number;
  } | null;
  model: string;
  /** False means the jobs would run and record warnings instead of embedding. */
  ollamaConfigured: boolean;
}

/**
 * Rebuilding the corpus's table grids, costed before it is started.
 *
 * `pending` is only what the button can do. `staleText` and `withoutBytes` are reported
 * beside it rather than folded in: a grid's offsets index the stored body, so text from
 * another extractor needs re-extracting first, and folded in they would leave a count that
 * never reaches zero.
 */
export interface TablesEstimate {
  tablesVersion: string;
  extractorVersion: string;
  total: number;
  built: number;
  pending: number;
  staleText: number;
  withoutBytes: number;
  queued: number;
  running: number;
  paused: boolean;
}

/**
 * One filing of a situation that holds text from an older extractor: `stale` and
 * `current` count its documents at an older and the current extractor version, and
 * `inFlight` says a text extraction for it is queued or running.
 */
export interface ReextractFiling {
  accession: string;
  formType: string;
  filingDate: string;
  stale: number;
  current: number;
  inFlight: boolean;
}

/**
 * Sending one situation's filings back through text extraction.
 *
 * A stale body moves every offset after its first changed character, so stored citations
 * resolve into text that no longer exists and a deep read over it produces quotes that
 * cannot be verified.
 *
 * `alreadyCurrent` and `noText` are stated beside the list rather than folded into it. A
 * filing never extracted at all is a fetch problem this cannot fix.
 */
export interface ReextractEstimate {
  extractorVersion: string;
  filings: ReextractFiling[];
  alreadyCurrent: number;
  noText: number;
}

/**
 * The outcome of re-extracting a situation's filings: how many filings were queued out of
 * those considered, and the estimate before and after.
 */
export interface ReextractResult {
  queued: number;
  considered: number;
  before: ReextractEstimate;
  after: ReextractEstimate;
}

/**
 * Re-running the Screen over text that is already stored. No EDGAR request and no Claude
 * call, so what is quoted is filings, not money or hours.
 */
export interface RetriageEstimate {
  screenVersion: string;
  /** Filings with stored text: the population a pass can run over at all. */
  total: number;
  screened: number;
  pending: number;
  /** Filings whose text was never stored, so no pass can reach them. */
  withoutText: number;
  queued: number;
  running: number;
  paused: boolean;
  lastScreenedAt: string | null;
}

/**
 * Filings already in the corpus that reconciliation would look at again: candidates for
 * a situation that are attached to none.
 */
export interface AttachmentBackfillEstimate {
  pending: number;
  inFlight: number;
}

/**
 * What re-running the free terms pass under today's patterns would cover.
 *
 * `extract_terms` is enqueued under a key with no version and a successful job is never
 * deleted, so `TERMS_VERSION` is what makes a pattern change re-read a pair.
 */
export interface TermsRerunEstimate {
  termsVersion: string;
  /** Primary attachments whose filing has stored text: what a pass can read at all. */
  total: number;
  read: number;
  pending: number;
  /** Primary attachments with no stored text, so no pass can reach them. */
  withoutText: number;
  /** Relevant-not-subject attachments, which the pass declines by design. */
  relevantOnly: number;
  queued: number;
  running: number;
  paused: boolean;
  lastReadAt: string | null;
}

/**
 * What the terms backfill would fetch.
 *
 * A Form 10 information statement is filed "Subject to Completion" with its record and
 * distribution dates blank in every version; the parent announces them in an 8-K once the
 * registration is effective. The historical walk fetches `10-12B` and `10-12B/A` only, so
 * for a situation completed years ago nothing in the corpus can supply the dates.
 */
export interface TermsBackfillEstimate {
  /** Situations with a Form 10 and a missing date, not already queued. */
  pending: number;
  /**
   * Of those, the ones with no parent CIK: a subset of `pending`, not a deduction from it.
   * The SpinCo's own current reports can still restate the dates; these are only the least
   * likely to find them.
   */
  parentUnknown: number;
  /** Queued or running from an earlier press. */
  inFlight: number;
}

/**
 * A company carrying more than one live situation, with what tells them apart.
 *
 * A proposal and not a finding: one parent can run several separations at once, so the
 * panel shows the discriminating facts (a ticker, a distribution date, an expected window)
 * and the owner decides.
 */
export interface DuplicateCandidate {
  id: string;
  spincoName: string;
  spincoTicker: string | null;
  stage: string;
  disposition: string;
  announcedOn: string | null;
  recordDate: string | null;
  distributionDate: string | null;
  ratio: string | null;
  expectedCompletion: string | null;
  filings: Array<{ accession: string; cik: string; formType: string; filingDate: string }>;
  factCount: number;
}

/**
 * What the code would do, computed server-side and stopped short of the write, so the
 * browser holds no second copy of the grouping rule the merge runs.
 */
export interface MergeProposal {
  keeperId: string;
  foldIds: string[];
  /**
   * `identity` — a shared ticker, terms or distribution date, which names one
   * distribution and no other. `window` — only a shared expected completion, which two
   * separations by one parent can genuinely share. The panel says which.
   */
  strength: "identity" | "window";
  reason: string;
}

/**
 * One company with more than one live situation, the situations, and the merges proposed
 * for them.
 */
export interface DuplicateGroup {
  cik: string;
  company: string;
  situations: DuplicateCandidate[];
  proposals: MergeProposal[];
}

/**
 * One filing parked because it could match more than one of its filer's situations.
 * `candidates` is how many it could match; `attempts` is how often placement was tried.
 */
export interface UnplacedItem {
  accession: string;
  cik: string;
  company: string | null;
  formType: string;
  filingDate: string;
  candidates: number;
  reason: string;
  attempts: number;
  firstSeenAt: string;
}

/**
 * Totals across every parked filing, and the first `limit` of them.
 *
 * `stuck` counts filings tried more than twice; `oldestDays` is the age of the
 * longest-parked one; `awaitingRead` counts placement jobs pending or running.
 */
export interface UnplacedReport {
  total: number;
  companies: number;
  stuck: number;
  oldestDays: number | null;
  awaitingRead: number;
  limit: number;
  items: UnplacedItem[];
}

/** The outcome of folding duplicate situations into one keeper. */
export interface MergeResult {
  keeperId: string;
  merged: string[];
  filingsMoved: number;
  factsMoved: number;
  /** The owner's own overrides, reported rather than relocated by an automated step. */
  userFactsNotMoved: Array<{ situationId: string; kind: string }>;
  announcedOn: string | null;
  warnings: string[];
}

/**
 * One filing as the current Screen judges it, with the rule hit counts. `screened` is
 * false when it has not been re-judged at this version yet.
 */
export interface RetriageFilingReport {
  accession: string;
  companyName: string | null;
  formType: string;
  filingDate: string;
  eventHits: number;
  strongHits: number;
  weakHits: number;
  /** What the document said, verbatim. Never the pattern that matched it. */
  reasons: string[];
  screened: boolean;
}

/** A live situation none of whose filings today's Screen would pass, with those filings. */
export interface UnsupportedSituation {
  situationId: string;
  spincoName: string;
  stage: string;
  confidence: number;
  announcedOn: string | null;
  filings: RetriageFilingReport[];
}

/**
 * Situations resting on no filing today's gate would pass — a list to read, never an
 * action taken. `pendingReview` is what stops a half-finished pass reading as a
 * finished one.
 */
export interface UnsupportedReport {
  screenVersion: string;
  situations: number;
  supported: number;
  pendingReview: number;
  items: UnsupportedSituation[];
}

/** A filing attached to a situation, with its role there and what it changed. */
export interface SituationFilingResponse {
  accession: string;
  cik: string;
  companyName: string | null;
  formType: string;
  filingDate: string;
  role: string;
  /**
   * Whether this situation is what the filing is about. False means it bears on this
   * separation without being its subject (a parent's quarterly release covering two of
   * them), and no fact here came from it. Absent from an older server; read it as true.
   */
  isPrimary?: boolean;
  ingestState: string;
  items: string[];
  primaryDocument: string | null;
  documentCount: number;
  /**
   * What this filing changed about the situation's terms.
   *
   * `[]` and absent are different: an empty list means the filing was read and moved
   * nothing, while a filing nothing has read has no facts pointing at its documents at
   * all. Optional here so an older server degrades to "no changes shown" rather than
   * throwing on a field it does not send.
   */
  changes?: FilingTermChange[];
}

/** A company as EDGAR describes it, with its tickers and the situations it appears on. */
export interface CompanyResponse {
  cik: string;
  name: string;
  sic: string | null;
  sicDescription: string | null;
  stateOfIncorporation: string | null;
  ein: string | null;
  formerNames: Array<{ name: string; from: string; to: string }>;
  tickers: Array<{ ticker: string; exchange: string | null; isCurrent: boolean }>;
  firstSeen: string | null;
  lastSeen: string | null;
  situations: Array<{ id: string; role: string }>;
  /**
   * This company's own registration of its own class under Section 12(b) — whether it
   * has ever been somebody else's SpinCo. Null is the ordinary answer and a real one.
   */
  ownForm10: { accession: string; filedOn: string } | null;
}

/** One document of a filing, holding the whole text or a slice of it. */
export interface DocumentResponse {
  id: string;
  accession: string;
  sequence: number;
  documentType: string | null;
  filename: string;
  description: string | null;
  /** The whole document's length, whatever slice of it `text` happens to be. */
  charCount: number;
  /**
   * Where `text` begins in the whole document, in the same UTF-16 offsets every citation
   * uses.
   *
   * **This is the server's answer, not the client's request.** Offsets are clamped rather
   * than rejected, so a reader that assumed the offsets it asked for would convert every
   * citation into the wrong passage with nothing throwing. Read the origin off the response.
   */
  charStart: number;
  charEnd: number;
  text: string;
}

/**
 * One heading in a document's outline and the span it covers. `chars` is the span's
 * length; `method` names how the heading was found.
 */
export interface OutlineSectionResponse {
  title: string;
  /** Absolute, into the whole document. Never an index into a loaded slice. */
  charStart: number;
  charEnd: number;
  chars: number;
  level: 1 | 2;
  method: "toc" | "caps" | "item";
}

/**
 * A section a separation thesis returns to, named by the server so that the vocabulary
 * lives in one place with the fixtures that test it. See server/src/ingest/keySections.ts.
 */
export interface KeySectionResponse extends OutlineSectionResponse {
  kind: string;
  label: string;
  why: string;
}

/** A document's map: its outline, the key sections named in it, and why it may have none. */
export interface OutlineResponse {
  accession: string;
  sequence: number;
  filename: string;
  documentType: string | null;
  charCount: number;
  sections: OutlineSectionResponse[];
  keySections: KeySectionResponse[];
  runningHeaders: string[];
  tableOfContents: { charStart: number; charEnd: number; entries: number } | null;
  /** Why a document has no map — an image-only exhibit, a wall of table cells. */
  warnings: string[];
}

/**
 * A filing and the documents it contains. A document's `charCount` is null when no text
 * was extracted from it.
 */
export interface FilingResponse {
  accession: string;
  cik: string;
  companyName: string | null;
  formType: string;
  filingDate: string;
  periodOfReport: string | null;
  items: string[];
  ingestState: string;
  documents: Array<{
    id: string;
    sequence: number;
    documentType: string | null;
    filename: string;
    description: string | null;
    byteSize: number | null;
    charCount: number | null;
  }>;
}

/** The signed-in owner. */
export interface Me {
  login: string;
  avatarUrl: string | null;
  /**
   * True when the server is not enforcing a session: development with no OAuth
   * configured.
   */
  unguarded: boolean;
}

/**
 * The job queue's state: counts by state and kind, the workers and executors holding
 * claims, what is paused, the spend so far and the most recent failures.
 */
export interface QueueState {
  byState: Record<string, number>;
  byKind: Record<string, number>;
  oldestPendingSeconds: number | null;
  /**
   * What makes `running` readable. One worker with a stale claim is a deploy that
   * interrupted a job and clears itself; two workers is a stray process. The running
   * count alone cannot tell those apart.
   */
  workers: number;
  staleClaims: number;
  /**
   * Claude Code sessions holding a claim. Counted apart from `workers` because they mean
   * opposite things: a second worker is a stray process, a second executor is the agent
   * path working as designed.
   */
  executors: number;
  /** Kinds no worker will claim right now. Paused work is skipped, never dropped. */
  pausedKinds: string[];
  /** Amounts are decimal strings summed in Postgres — money is never a float. */
  spend: {
    extractions: number;
    /** Of those, how many ran on a subscription through `/api/extractions` — $0, real tokens. */
    onSubscription: number;
    extractionUsd: string;
    questions: number;
    questionsUsd: string;
    totalUsd: string;
    since: string | null;
  };
  /**
   * What is parked, as the same record the live queue is made of, so the Failed panel and
   * the queue tail render through one component. Capped; `failuresTotal` is the real count,
   * because a capped list reads exactly like a complete one.
   */
  recentFailures: QueuedJob[];
  failuresTotal: number;
}

/**
 * One finished job, as both the Ops tail and the history page render it.
 *
 * `outcome` is what the handler said the run produced. It is null for a failure, whose
 * account is `lastError`, for a handler with nothing worth saying, and for a job finished
 * before outcomes were recorded.
 *
 * `ticker` and `situation` are null where none exists: a daily scan is about a whole
 * trading day and has no filer, and a filing that never reconciled has no situation.
 */
export interface FinishedJob {
  id: string;
  kind: string;
  subject: string | null;
  state: "done" | "failed";
  finishedAt: string;
  secondsAgo: number;
  durationSeconds: number | null;
  attempts: number;
  lastError: string | null;
  outcome: string | null;
  ticker: string | null;
  company: string | null;
  /** `alsoOn` counts the OTHER situations carrying this filing, so 0 means only this one. */
  situation: { id: string; name: string; alsoOn: number } | null;
}

/**
 * One search result — a document, or a passage inside one.
 *
 * `charStart` is null when only the phrase half found it: FTS ranks whole documents and
 * never produced an offset, and inventing one would present a citation the evidence does
 * not support. A row without offsets opens the document; a row with them opens the
 * passage.
 */
export interface SearchHit {
  chunkId: string | null;
  documentId: string;
  accession: string;
  formType: string | null;
  filename: string;
  companyName: string | null;
  filingDate: string;
  charStart: number | null;
  charEnd: number | null;
  /** FTS wraps its match in « » guillemets; a vector hit is the raw passage. */
  snippet: string;
  matchedBy: Array<"phrase" | "meaning">;
  score: number;
}

/** The results of a corpus search, and which halves of the search ran. */
export interface SearchResults {
  query: string;
  items: SearchHit[];
  granularity: "document" | "passage";
  /**
   * Which halves ran. A corpus with no embeddings still searches by phrase, and a reader
   * whose paraphrase came back empty needs to know the semantic half is not running.
   */
  searched: { phrase: boolean; meaning: boolean; meaningUnavailable?: string };
}

/**
 * An image a document references. `src` is a bare filename; the proxy validates it
 * against the filing's own manifest, so nothing here needs to be trusted.
 */
export interface DocumentImageResponse {
  src: string;
  alt: string | null;
  /** What the filer declared — a logo and a scanned page want opposite treatment. */
  width: number | null;
  height: number | null;
}

/**
 * The grids for one span of a document, and which of three things an empty list means.
 *
 * `not_built` — extracted before the sidecar existed. `stale` — the text has been
 * re-extracted under a newer extractor, or the table rules changed, so the offsets may
 * no longer point where they did and the grids are withheld. `current` with nothing in
 * it — this span genuinely has no table.
 */
export interface DocumentTablesResponse {
  accession: string;
  sequence: number;
  /** The span actually answered for, clamped. The one to trust, not the one asked for. */
  charStart: number;
  charEnd: number;
  state: "current" | "stale" | "not_built";
  tables: DocumentTable[];
}

/** One page of the completion history, newest first. */
export interface JobHistoryPage {
  items: FinishedJob[];
  /** Pass back as `cursor`. Null on the last page. */
  nextCursor: string | null;
  /** Counted on the first page only — a later page does not recompute it. */
  total: number | null;
  /**
   * True when the search term matched more filings or situations than the resolver
   * carries, so this is a subset. Rendered, because a truncated result reads exactly
   * like a complete one.
   */
  truncatedMatches: boolean;
}

/**
 * One tolerant-parse warning, with whoever it is about resolved.
 *
 * `subjectId` is one opaque column whose meaning depends on the scope — an accession, a
 * CIK, a document id, a trading date. The server reads it and answers with what a person
 * recognises; the raw value stays, because it is the thing to paste into a query.
 *
 * `document` is populated only where the subject IS a document, never guessed from a
 * filing: a filing has many documents and picking one would be an invention. That is the
 * `embed` case, where the subject is a bare UUID and the panel could otherwise say a
 * document was not indexed without saying which.
 */
export interface WarningItem {
  id: string;
  scope: string;
  subjectId: string | null;
  message: string;
  createdAt: string;
  ticker: string | null;
  company: string | null;
  situation: { id: string; name: string; alsoOn: number } | null;
  document: { filename: string; documentType: string | null; accession: string } | null;
}

/**
 * One job that has not run yet, or has run and parked: the queue's own record.
 *
 * It carries the same resolved context a finished job does: whose filing this is, which
 * separation it would move, and why it is not running.
 *
 * A claimed job reports `pending`: it has not left the queue. The lease, the worker and
 * the chunk progress belong to `OpsActivity.running`.
 */
export interface QueuedJob {
  id: string;
  kind: string;
  /** The accession, date, quarter or exhibit — never a bare UUID where a name exists. */
  subject: string | null;
  state: "pending" | "failed";
  /** When the work was enqueued, which for a backfill quarter is not when it will run. */
  enqueuedAt: string;
  /**
   * How long this has been claimable, signed. **Negative means not yet runnable**, a retry
   * waiting out its backoff. Clamped to zero, a queue that is waiting would read as one that
   * is stuck.
   */
  runnableForSeconds: number;
  /** Why one row sits above another: claim order is `priority DESC, run_after`. */
  priority: number;
  attempts: number;
  maxAttempts: number;
  /** Times the reaper took this back from a worker that stopped. Not a failure count. */
  reclaims: number;
  lastError: string | null;
  /** When this was parked. Null for anything still live. */
  failedAt: string | null;
  paused: boolean;
  ticker: string | null;
  company: string | null;
  situation: { id: string; name: string; alsoOn: number } | null;
}

/**
 * One bounded page of the live queue — deliberately not paginated.
 *
 * The finished history is append-only, so a keyset cursor over it is stable. The live
 * queue drains from the front while you read it, so page two of a cursor walk is a page
 * of rows that have shifted underneath the boundary; it would repeat and skip with no way
 * for the reader to tell. `truncated` says when the page is a prefix of `total`.
 */
export interface QueueListing {
  items: QueuedJob[];
  total: number;
  truncated: boolean;
  /** The search term matched more than the resolver will carry — said, never applied. */
  truncatedMatches: boolean;
}

/**
 * What the machine is doing right now: the fast poll's payload.
 *
 * Separate from `QueueState`, which counts every row in the queue (finished jobs are
 * never deleted) and sums two spend ledgers, and so is too costly to poll this often.
 */
export interface OpsActivity {
  /** The server's clock when this was measured. Rendered, so a stale panel says so. */
  observedAt: string;
  running: Array<{
    id: string;
    kind: string;
    /** The accession, date, quarter or exhibit — never a bare UUID where a name exists. */
    subject: string | null;
    workerId: string | null;
    /** Since the job was claimed. Null when no claim time was recorded. */
    runningSeconds: number | null;
    /** Since the worker last renewed its claim — the proof that it is still alive. */
    leaseSeconds: number | null;
    leaseIsStale: boolean;
    attempt: number;
    maxAttempts: number;
    /** Chunks written so far by an `embed_document`, against an estimate for the document. */
    progress: { chunksWritten: number; estimatedChunks: number | null } | null;
    /** When the job was claimed. Null when no claim time was recorded. */
    startedAt: string | null;
    ticker: string | null;
    company: string | null;
    situation: { id: string; name: string; alsoOn: number } | null;
    document: { filename: string; documentType: string | null; accession: string } | null;
  }>;
  /** Which stage the backlog is sitting in. A global count cannot say that. */
  backlog: Array<{
    kind: string;
    pending: number;
    running: number;
    failed: number;
    /** Negative means not runnable yet — a retry waiting out its backoff. */
    oldestPendingSeconds: number | null;
    paused: boolean;
  }>;
  /**
   * What runs next, in claim order: the head of `jobs_claim_idx`, capped.
   *
   * `backlog` above is the total and this is the record, and neither replaces the other: a
   * capped tail understates the queue, and a per-kind count cannot say whose filing waits.
   */
  queued: QueuedJob[];
  recent: FinishedJob[];
  /** False means no finish time has ever been recorded — not that nothing has run. */
  completionsRecorded: boolean;
  throughput: { windowMinutes: number; completed: number; failed: number };
  /** `chunksPerSecond` is null when nothing was written: an absence, not a rate of zero. */
  embedding: {
    windowMinutes: number;
    chunks: number;
    chunksPerSecond: number | null;
    lastChunkAt: string | null;
    lastChunkSecondsAgo: number | null;
  };
  workers: number;
  staleClaims: number;
  executors: number;
  pausedKinds: string[];
}

/** The size of a deep read, quoted before it runs. */
export interface ExtractEstimate {
  /** Prompt tokens, from the document's character count — the button's number, not the bill's. */
  tokens: number;
  /** Dollars, as a decimal string. */
  usd: string;
}

/**
 * The unreviewed situations no read has reached. `reconcile_filing` queues a read only for
 * a filing recent enough to be news, so older situations wait here, behind a button with
 * its count quoted first.
 */
export interface ExtractBacklogState {
  estimate: {
    /** Unreviewed, readable, and never read — the only number the button acts on. */
    pending: number;
    /** Unreviewed but with no extracted text, so no read can reach them. */
    unreadable: number;
    inFlight: number;
    /**
     * Read already and still unreviewed: the owner's queue rather than a backlog. A read
     * that confirms a separation does not touch `disposition`, which is the owner's.
     */
    alreadyRead: number;
    /**
     * Every situation a re-read would take, already-read ones included. It overlaps
     * `pending` rather than adding to it.
     */
    rereadable: number;
    /**
     * What a re-read excludes: judged never to have been a separation, and holding a
     * filing it could otherwise have read. Stated so "everything" does not quietly
     * mean something narrower.
     */
    dismissed: number;
  };
  executor: "subscription";
}

/** Which population a press queues. `all` includes situations already read. */
export type ExtractBacklogScope = "unread" | "all";

/**
 * The outcome of a press on the read backlog: how many reads were queued, how many of the
 * pressed population remain unqueued, and the estimate afterwards.
 */
export interface ExtractBacklogQueued {
  queued: number;
  scope: ExtractBacklogScope;
  remaining: number;
  unreadable: number;
  estimate: ExtractBacklogState["estimate"];
}

/** A filing in a situation that a deep read could take, with its size. */
export interface ExtractionCandidate {
  accession: string;
  formType: string;
  filingDate: string;
  filename: string;
  charCount: number;
  estimate: ExtractEstimate;
  /** Null when nothing has read this filing. A re-read of one that has is free. */
  lastReadAt: string | null;
}

/**
 * What a deep read of this situation would read, and what it would cost.
 *
 * `target` is null when nothing in the situation has extracted text yet, most often a
 * Form 10 still in the fetch queue. `runs` is what has already been paid for; a filing
 * that appears there re-reads for nothing, because the stored response is reused.
 */
export interface ExtractionState {
  target: {
    accession: string;
    formType: string;
    filingDate: string;
    filename: string;
    charCount: number;
    estimate: ExtractEstimate;
    /**
     * The filing this read will be diffed against, or null for a whole read. An amendment
     * restates the entire document, so only what changed is sent; `charCount` is the whole
     * document either way.
     */
    priorAccession: string | null;
  } | null;
  /** Every filing here that could be read, best first. `[0]` is the default target. */
  candidates: ExtractionCandidate[];
  /** Always "subscription": extraction never runs on the metered API key. */
  executor: "subscription";
  /** False, always. The estimate is the size of the document, not a bill. */
  billed: boolean;
  /** False means nothing can claim a queued read — the press would sit forever. */
  agentConfigured: boolean;
  queued: { jobId: string; state: string } | null;
  runs: Array<{
    /** Which filing was read; a situation can have several. */
    accession: string;
    costUsd: string;
    at: string;
    model: string;
    outputTokens: number;
    /** `agent` ran on a subscription; `worker` is a historical API-key read. */
    executor: string;
  }>;
}

/**
 * The outcome of queueing a deep read: the job, or `alreadyQueued` when one was waiting,
 * and `previousRun` when the filing has been read before.
 */
export interface ExtractQueued {
  jobId: string | null;
  accession: string;
  formType: string;
  estimate: ExtractEstimate;
  alreadyQueued: boolean;
  previousRun: { costUsd: string; at: string } | null;
}

/**
 * The typed client for the REST contract, one method per route.
 *
 * Every method rejects with an `ApiError` when the server answers with a non-2xx status.
 */
export const api = {
  me: () => request<Me>("/api/auth/me"),
  logout: () => request<{ ok: true }>("/api/auth/logout", { method: "POST" }),

  situations: (filters: SituationFilters = {}) =>
    request<ListResponse<SituationSummary>>(`/api/situations${query({ ...filters })}`),
  situation: (id: string) => request<SituationDetail>(`/api/situations/${id}`),
  company: (cik: string) => request<CompanyResponse>(`/api/companies/${cik}`),
  companyFilings: (cik: string, limit = 25) =>
    request<ListResponse<FilingResponse>>(`/api/companies/${cik}/filings${query({ limit })}`),
  patchSituation: (id: string, patch: Record<string, unknown>) =>
    request<SituationSummary>(`/api/situations/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  timeline: (id: string) => request<ListResponse<SituationEvent>>(`/api/situations/${id}/timeline`),
  facts: (id: string, history = false) =>
    request<ListResponse<FactResponse>>(`/api/situations/${id}/facts${query({ history })}`),
  overrideFact: (id: string, kind: string, value: unknown, note?: string) =>
    request<FactResponse>(`/api/situations/${id}/facts/${kind}`, {
      method: "PUT",
      body: JSON.stringify({ value, note }),
    }),
  flags: (id: string, includeDismissed = false) =>
    request<ListResponse<Flag>>(`/api/situations/${id}/flags${query({ includeDismissed })}`),
  dismissFlag: (id: string, flagId: string, reason?: string) =>
    request<{ ok: true }>(`/api/situations/${id}/flags/${flagId}/dismiss`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),
  reextract: (id: string) =>
    request<{ estimate: ReextractEstimate }>(`/api/situations/${id}/reextract`),
  runReextract: (id: string) =>
    request<ReextractResult>(`/api/situations/${id}/reextract`, { method: "POST" }),
  extractBacklog: () => request<ExtractBacklogState>("/api/extract/backlog"),
  startExtractBacklog: (scope: ExtractBacklogScope = "unread") =>
    request<ExtractBacklogQueued>("/api/extract/backlog", {
      method: "POST",
      body: JSON.stringify({ scope }),
    }),
  extractionState: (id: string, accession?: string) =>
    request<ExtractionState>(
      `/api/situations/${id}/extract${accession ? `?accession=${encodeURIComponent(accession)}` : ""}`,
    ),
  extract: (id: string, accession?: string) =>
    request<ExtractQueued>(`/api/situations/${id}/extract`, {
      method: "POST",
      body: JSON.stringify(accession ? { accession } : {}),
    }),
  thesis: (id: string) => request<Thesis>(`/api/situations/${id}/thesis`),
  saveThesis: (id: string, bodyMd: string) =>
    request<Thesis>(`/api/situations/${id}/thesis`, {
      method: "PUT",
      body: JSON.stringify({ bodyMd }),
    }),
  thesisVersions: (id: string) =>
    request<ListResponse<ThesisVersion>>(`/api/situations/${id}/thesis/versions`),
  thesisVersion: (id: string, versionId: string) =>
    request<ThesisVersion>(`/api/situations/${id}/thesis/versions?versionId=${versionId}`),

  excerpts: (id: string) => request<ListResponse<Excerpt>>(`/api/situations/${id}/excerpts`),
  deleteExcerpt: (id: string, excerptId: string) =>
    request<void>(`/api/situations/${id}/excerpts/${excerptId}`, { method: "DELETE" }),
  commentExcerpt: (id: string, excerptId: string, comment: string | null) =>
    request<Excerpt>(`/api/situations/${id}/excerpts/${excerptId}`, {
      method: "PATCH",
      body: JSON.stringify({ comment }),
    }),

  questions: (id: string, includeResolved = false) =>
    request<ListResponse<OpenQuestion>>(
      `/api/situations/${id}/questions${includeResolved ? "?includeResolved=true" : ""}`,
    ),
  addQuestion: (id: string, body: string) =>
    request<OpenQuestion>(`/api/situations/${id}/questions`, {
      method: "POST",
      body: JSON.stringify({ body }),
    }),
  resolveQuestion: (id: string, questionId: string, resolutionMd: string | null) =>
    request<OpenQuestion>(`/api/situations/${id}/questions/${questionId}`, {
      method: "PATCH",
      body: JSON.stringify({ resolutionMd }),
    }),
  deleteQuestion: (id: string, questionId: string) =>
    request<void>(`/api/situations/${id}/questions/${questionId}`, { method: "DELETE" }),

  /**
   * The company's reported history. Free — one EDGAR request buys everything — so a
   * missing company is fetched on ask rather than behind a button, and the response
   * says `fetching` while that happens.
   */
  situationFinancials: (id: string, concept: string, basis: string, years = 10) =>
    request<SituationFinancials>(
      `/api/situations/${id}/financials?concept=${concept}&basis=${basis}&years=${years}`,
    ),
  companyConcepts: (cik: string, search?: string) =>
    request<{ status: string; items: AvailableConcept[] }>(
      `/api/companies/${cik}/financials/concepts${search ? `?search=${encodeURIComponent(search)}` : ""}`,
    ),
  refreshFinancials: (cik: string) =>
    request<{ cik: string; jobId: string | null }>(`/api/companies/${cik}/financials/refresh`, {
      method: "POST",
      body: JSON.stringify({}),
    }),

  situationFilings: (id: string) =>
    request<ListResponse<SituationFilingResponse>>(`/api/situations/${id}/filings`),

  situationInsiders: (id: string) =>
    request<InsiderActivity>(`/api/situations/${id}/insiders`),

  situationIndexes: (id: string) =>
    request<{ parent: IndexMembershipResult; spinco: IndexMembershipResult }>(
      `/api/situations/${id}/indexes`,
    ),

  situationMarketCap: (id: string) =>
    request<{ parent: MarketCapResult; spinco: MarketCapResult }>(
      `/api/situations/${id}/marketcap`,
    ),

  filing: (accession: string) => request<FilingResponse>(`/api/filings/${accession}`),
  /**
   * The document, or a named span of it. The span is absolute — the offsets a citation,
   * an outline section and a flag's evidence all speak in — and the response says which
   * span it actually served, which is the one to trust.
   */
  document: (accession: string, sequence: number, range?: { charStart: number; charEnd: number }) =>
    request<DocumentResponse>(
      `/api/filings/${accession}/documents/${sequence}${range ? query(range) : ""}`,
    ),
  documentOutline: (accession: string, sequence: number) =>
    request<OutlineResponse>(`/api/filings/${accession}/documents/${sequence}/outline`),

  /**
   * The tables in the span being read, as grids, scoped to the same range as the text.
   *
   * `state` says what an empty list means (see `DocumentTablesResponse`). For `not_built` and
   * `stale`, render the plain text, which is always correct and merely less legible.
   */
  documentTables: (
    accession: string,
    sequence: number,
    range?: { charStart: number; charEnd: number },
  ) =>
    request<DocumentTablesResponse>(
      `/api/filings/${accession}/documents/${sequence}/tables${range ? query(range) : ""}`,
    ),

  /**
   * What embedding the existing corpus would cost, in hours of the machine rather than
   * money. Quoted before the press, because a pass can occupy the host for hours.
   */
  embedBackfillEstimate: () => request<BackfillEstimate>("/api/embed/backfill"),
  form10Backfill: (range: { from?: string; to?: string } = {}) =>
    request<Form10BackfillEstimate>(`/api/backfill/form10${query(range)}`),
  startForm10Backfill: (range: { from?: string; to?: string } = {}) =>
    request<{ queued: number; alreadyQueued: number; estimate: Form10BackfillEstimate }>(
      "/api/backfill/form10",
      { method: "POST", body: JSON.stringify(range) },
    ),
  /** The free terms pass, re-run over pairs read under older patterns. */
  termsRerun: () => request<TermsRerunEstimate>("/api/terms/rerun"),
  startTermsRerun: (opts: { limit?: number } = {}) =>
    request<{
      queued: number;
      alreadyQueued: number;
      remaining: number;
      estimate: TermsRerunEstimate;
    }>("/api/terms/rerun", { method: "POST", body: JSON.stringify(opts) }),

  /** What re-running the current Screen over the stored corpus would cover. */
  retriage: () => request<RetriageEstimate>("/api/retriage"),
  startRetriage: (opts: { limit?: number } = {}) =>
    request<{
      queued: number;
      alreadyQueued: number;
      remaining: number;
      estimate: RetriageEstimate;
    }>("/api/retriage", { method: "POST", body: JSON.stringify(opts) }),
  /** Reported, never acted on: the pass retires nothing on its own. */
  unsupportedSituations: () => request<UnsupportedReport>("/api/retriage/unsupported"),

  duplicateSituations: () => request<{ groups: DuplicateGroup[] }>("/api/situations/duplicates"),
  unplacedFilings: () => request<UnplacedReport>("/api/situations/unplaced"),
  mergeSituations: (body: {
    keeperId: string;
    mergeIds: string[];
    reason?: string;
    approvedProposal?: boolean;
  }) =>
    request<MergeResult>("/api/situations/merge", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  attachmentBackfill: () =>
    request<{ estimate: AttachmentBackfillEstimate }>("/api/backfill/attachments"),
  startAttachmentBackfill: (opts: { limit?: number } = {}) =>
    request<{
      queued: number;
      considered: number;
      before: AttachmentBackfillEstimate;
      after: AttachmentBackfillEstimate;
    }>("/api/backfill/attachments", { method: "POST", body: JSON.stringify(opts) }),

  /** See `TermsBackfillEstimate`: the dates a Form 10 could never have carried. */
  termsBackfill: () => request<{ estimate: TermsBackfillEstimate }>("/api/backfill/terms"),
  startTermsBackfill: (opts: { limit?: number } = {}) =>
    request<{ queued: number; selected: number; before: TermsBackfillEstimate }>(
      "/api/backfill/terms",
      { method: "POST", body: JSON.stringify(opts) },
    ),

  /** What rebuilding the corpus's table grids would cover. */
  tablesBackfill: () => request<TablesEstimate>("/api/tables/backfill"),
  startTablesBackfill: (opts: { limit?: number } = {}) =>
    request<{
      queued: number;
      alreadyQueued: number;
      remaining: number;
      estimate: TablesEstimate;
    }>("/api/tables/backfill", { method: "POST", body: JSON.stringify(opts) }),

  embedBackfill: (opts: { situationId?: string; limit?: number } = {}) =>
    request<{ queued: number; alreadyQueued: number; remaining: number; estimate: BackfillEstimate }>(
      "/api/embed/backfill",
      { method: "POST", body: JSON.stringify(opts) },
    ),

  /**
   * What a question at this depth would read, and what that would cost. `askScopeQuery` is
   * the one place a scope becomes query parameters; the server's `toScope` reads them back.
   */
  askPreflight: (depth: string, scope: AskScope) =>
    request<AskPreflight>(`/api/ask/preflight${query({ depth, ...askScopeQuery(scope) })}`),
  askThreads: (situationId?: string) =>
    request<{ items: AskThreadSummary[] }>(
      `/api/ask/threads${situationId ? `?situationId=${situationId}` : ""}`,
    ),
  askThread: (id: string) =>
    request<{ id: string; title: string; items: AskStoredMessage[] }>(`/api/ask/threads/${id}`),
  deleteAskThread: (id: string) => request<void>(`/api/ask/threads/${id}`, { method: "DELETE" }),

  jobs: () => request<QueueState>("/api/jobs"),
  /**
   * The fast poll: what is running, what is queued behind it, what just finished. See
   * `server/src/db/activity.ts` for which index each part reads.
   */
  opsActivity: () => request<OpsActivity>("/api/ops/activity"),

  /**
   * The completion history, page by page; the panel's rows are its head.
   *
   * Keyset pagination: `cursor` is the previous page's `nextCursor`, never an offset. `jobs`
   * is never pruned, so an offset page slows as it grows and repeats a row when something
   * finishes mid-scroll.
   */
  opsHistory: (params: {
    kind?: string;
    state?: "done" | "failed";
    q?: string;
    cursor?: string;
    limit?: number;
  } = {}) => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== "") search.set(key, String(value));
    }
    const query = search.toString();
    return request<JobHistoryPage>(`/api/ops/history${query ? `?${query}` : ""}`);
  },

  /** The kinds that actually appear in the history, so the filter offers only those. */
  opsHistoryKinds: () => request<{ kinds: string[] }>("/api/ops/history/kinds"),

  /**
   * The whole live queue, filtered — what the queue tail links out to.
   *
   * No cursor, on purpose: see `QueueListing`. One bounded page in claim order with the
   * total beside it is a true statement at the instant it was read, where a cursor walk
   * over a draining queue is not.
   */
  opsQueue: (
    params: { kind?: string; state?: "pending" | "failed"; q?: string; limit?: number } = {},
  ) => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== "") search.set(key, String(value));
    }
    const query = search.toString();
    return request<QueueListing>(`/api/ops/queue${query ? `?${query}` : ""}`);
  },

  /** The kinds with something live in the queue, so the filter offers what is there. */
  opsQueueKinds: () => request<{ kinds: string[] }>("/api/ops/queue/kinds"),

  /**
   * Cross-corpus search, hybrid: `matchedBy` says which half found each result.
   *
   * The two halves fail in opposite directions, so why something is in the list is part of
   * judging whether it belongs there. A phrase hit ranked the document and carries no
   * offsets; a meaning hit knows the passage.
   */
  search: (params: {
    q: string;
    limit?: number;
    situationId?: string;
    granularity?: "document" | "passage";
    formType?: string;
    after?: string;
    before?: string;
  }) => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== "") search.set(key, String(value));
    }
    return request<SearchResults>(`/api/search?${search.toString()}`);
  },

  /**
   * The images one document references, in document order. Read from the ORIGINAL bytes:
   * the extractor emits nothing for an `<img>`, so the sources are not in the text.
   */
  documentImages: (accession: string, sequence: number) =>
    request<{ images: DocumentImageResponse[]; source: "document" | "unavailable" }>(
      `/api/filings/${accession}/documents/${sequence}/images`,
    ),

  /** The forms something has actually been ingested for, with a filing count each. */
  searchForms: () =>
    request<{ items: Array<{ formType: string; filings: number }> }>("/api/search/forms"),
  /**
   * Stop or resume one kind of work. Paused jobs stay queued and run when the pause
   * lifts — pausing the daily scan accumulates one job per day rather than losing them.
   */
  pauseKind: (kind: string, paused: boolean) =>
    request<{ kind: string; paused: boolean; pausedKinds: string[] }>("/api/jobs/pause", {
      method: "POST",
      body: JSON.stringify({ kind, paused }),
    }),
  retryJob: (jobId: string) =>
    request<{ ok: true }>(`/api/jobs/${jobId}/retry`, { method: "POST" }),
  sync: (date?: string) =>
    request<{ jobId: string | null; date: string; alreadyQueued: boolean }>("/api/sync", {
      method: "POST",
      body: JSON.stringify(date ? { date } : {}),
    }),
  /**
   * Notification history and the channels this deployment can deliver through.
   *
   * `configured` is derived from what has a webhook rather than from a settings row:
   * for one user, a toggle that can disagree with the credential is a second source of
   * truth about the same fact.
   */
  notifications: () =>
    request<{ configured: string[]; notifications: NotificationResponse[] }>("/api/notify"),
  warnings: (scope?: string) =>
    request<ListResponse<WarningItem>>(`/api/warnings${query({ scope })}`),

  /**
   * The warning history, page by page. Keyset, never an offset — `warnings` only grows,
   * and an offset page silently repeats or skips a row when something is recorded
   * mid-scroll.
   */
  warningHistory: (
    params: { scope?: string; q?: string; cursor?: string; limit?: number } = {},
  ) => request<ListResponse<WarningItem>>(`/api/warnings${query(params)}`),
  warningScopes: () => request<{ scopes: string[] }>("/api/warnings/scopes"),

  /** The notification history, page by page, on the same terms. */
  notifyHistory: (
    params: {
      kind?: string;
      delivery?: "delivered" | "undelivered";
      q?: string;
      cursor?: string;
      limit?: number;
    } = {},
  ) =>
    request<{ items: NotificationResponse[]; nextCursor: string | null; total: number | null }>(
      `/api/notify/history${query(params)}`,
    ),
  notifyKinds: () => request<{ kinds: string[] }>("/api/notify/kinds"),
  version: () => request<{ version: string; commit: string; builtAt: string }>("/api/version"),
  health: () =>
    request<{
      status: string;
      database: { ok: boolean; detail: string | null; size: DatabaseSize | null };
      ollama: { ok: boolean; detail: string | null };
      edgarBudgetRemaining: number;
    }>("/api/health"),
};

/** A stored Ask thread, as the thread list shows it. */
export interface AskThreadSummary {
  id: string;
  situationId: string | null;
  title: string;
  messageCount: number;
  /** Dollars, as a decimal string. */
  totalUsd: string;
  createdAt: string;
  updatedAt: string;
}

/** One stored message of an Ask thread, with the sources and quotes of an answer. */
export interface AskStoredMessage {
  id: string;
  role: "user" | "assistant";
  body: string;
  depth: string | null;
  model: string | null;
  sources: AskSource[];
  quotes: AskQuote[];
  entityWarning: string | null;
  usage: AskUsage | null;
  createdAt: string;
}

/**
 * Streams `POST /api/ask`.
 *
 * Hand-rolled rather than `EventSource`, which only does GET — the question, its scope
 * and its depth are a body, not a query string. The parser is the trivial half of SSE:
 * blank-line-delimited blocks with an `event:` and a `data:` line, which is all the
 * server emits.
 */
export async function askStream(
  body: {
    question: string;
    scope: Record<string, unknown>;
    depth: string;
    model?: string;
    threadId?: string;
  },
  on: { [K in keyof AskEventMap]?: (data: AskEventMap[K]) => void },
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch("/api/ask", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify(body),
    signal,
  });

  /**
   * A `whole`-depth question is queued for a Claude Code session rather than answered here.
   * The server says so with a 202 and a JSON body, which must be caught before the SSE
   * reader: fed to the event parser, it yields no events and the answer never arrives.
   */
  if (res.status === 202) {
    on.queued?.((await res.json()) as AskEventMap["queued"]);
    return;
  }

  if (!res.ok || !res.body) {
    // A 503 (no key) or a 400 answers with the ordinary error envelope, not a stream.
    let message = `Ask failed with ${res.status}.`;
    try {
      const parsed = (await res.json()) as ApiErrorBody;
      message = parsed.error?.message ?? message;
    } catch {
      /* a non-JSON body is itself the problem */
    }
    on.error?.({ code: "request_failed", message });
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // Everything up to the last blank line is complete; the remainder is a partial
    // frame that the next chunk finishes.
    let split = buffer.indexOf("\n\n");
    while (split !== -1) {
      const block = buffer.slice(0, split);
      buffer = buffer.slice(split + 2);
      split = buffer.indexOf("\n\n");

      const event = /^event: (.+)$/m.exec(block)?.[1];
      const data = /^data: (.+)$/m.exec(block)?.[1];
      if (!event || !data) continue;
      const handler = on[event as keyof AskEventMap] as ((d: unknown) => void) | undefined;
      try {
        handler?.(JSON.parse(data));
      } catch {
        /* a frame that will not parse is dropped rather than killing the stream */
      }
    }
  }
}

/** A notification Mosaic recorded, and the channels it was delivered to. */
export interface NotificationResponse {
  id: string;
  kind: string;
  subjectType: string | null;
  subjectId: string | null;
  message: { title: string; body: string | null; url: string | null; severity: string };
  createdAt: string;
  deliveredAt: string | null;
  channelsDelivered: string[];
}
